import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import YukiDocument from '../../models/yuki/YukiDocument';
import { DeleteLinkedObject, DeleteSubLinkToLinkedObjectToFile } from '../../shared/global-files/global-files.actions';
import { FetchDocumentById, VerifyYukiDocument } from '../yuki.actions';
import { YukiState } from '../yuki.state';

@Component({
    selector: 'app-invoice-information-modal',
    templateUrl: './invoice-information-modal.component.html',
    styleUrls: ['./invoice-information-modal.component.scss'],
})
export class InvoiceInformationModalComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();
    isUpdatingVerification = false;

    @Input() yukiDocumentId: string;
    @Input() linkedObjectId?: string;
    @Input() linkedChildLinkId?: string;
    @Input() linkedIsSublink = false;

    @Select(YukiState.documentForInformationModal)
    yukiDocument$: Observable<YukiDocument>;
    yukiDocument: YukiDocument;

    constructor(public activeModal: NgbActiveModal, private store: Store, private logger: NGXLogger, private toastr: ToastrService) {}

    ngOnInit(): void {
        this.fetchData();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    isVerified(): boolean {
        return !!(this.yukiDocument?.verified ?? this.yukiDocument?.isVerified);
    }

    hasRemovableLinkContext(): boolean {
        if (this.linkedIsSublink) {
            return !!this.linkedChildLinkId;
        }
        return !!this.linkedObjectId && !!this.yukiDocumentId;
    }

    getPrimaryActionLabel(): string {
        return 'Unverify & unlink';
    }

    onPrimaryAction(): void {
        this.removeAndUnverify();
    }

    removeAndUnverify(): void {
        if (!this.yukiDocument?.yukiId) {
            this.toastr.error('Document id not available for verification update.');
            return;
        }

        this.isUpdatingVerification = true;

        const removeLink$ = () => {
            if (this.linkedIsSublink && this.linkedChildLinkId) {
                return this.store.dispatch(new DeleteSubLinkToLinkedObjectToFile(this.linkedChildLinkId));
            }
            if (this.linkedObjectId && this.yukiDocumentId) {
                return this.store.dispatch(new DeleteLinkedObject(this.yukiDocumentId, this.linkedObjectId));
            }
            return of(null);
        };

        const onSuccess = () => {
            this.isUpdatingVerification = false;
            this.toastr.success('Link verwijderd.');
            this.activeModal.close();
        };

        const onError = () => {
            this.isUpdatingVerification = false;
            this.logger.error('Failed to remove link and/or unverify Yuki document.');
            this.toastr.error('Oops, something went wrong, Please try again later...');
        };

        const hasLinkContext = this.hasRemovableLinkContext();
        const verified = this.isVerified();

        if (verified && hasLinkContext) {
            this.subscriptions.add(
                this.store
                    .dispatch(new VerifyYukiDocument(this.yukiDocument.yukiId))
                    .pipe(switchMap(() => removeLink$()))
                    .subscribe({ next: onSuccess, error: onError }),
            );
            return;
        }

        if (verified) {
            this.subscriptions.add(this.store.dispatch(new VerifyYukiDocument(this.yukiDocument.yukiId)).subscribe({ next: onSuccess, error: onError }));
            return;
        }

        if (hasLinkContext) {
            this.subscriptions.add(removeLink$().subscribe({ next: onSuccess, error: onError }));
            return;
        }

        this.isUpdatingVerification = false;
        this.toastr.info('Document is al unverified en er is geen link om te verwijderen.');
    }

    toggleVerification(): void {
        if (!this.yukiDocument?.yukiId) {
            this.toastr.error('Document id not available for verification update.');
            return;
        }

        this.isUpdatingVerification = true;
        const nextStateIsVerified = !this.isVerified();

        this.subscriptions.add(
            this.store.dispatch(new VerifyYukiDocument(this.yukiDocument.yukiId)).subscribe({
                next: () => {
                    this.isUpdatingVerification = false;
                    this.toastr.success(nextStateIsVerified ? 'Yuki document successfully verified.' : 'Yuki document verification removed.');
                },
                error: () => {
                    this.isUpdatingVerification = false;
                    this.logger.error('Failed to update Yuki document verification.');
                    this.toastr.error('Oops, something went wrong, Please try again later...');
                },
            }),
        );
    }

    private fetchData() {
        this.subscriptions.add(
            this.yukiDocument$.subscribe((yukiDocument) => {
                if (!yukiDocument || yukiDocument.uuid !== this.yukiDocumentId) {
                    this.logger.debug('Fetching Yuki document by id');
                    this.store.dispatch(new FetchDocumentById(this.yukiDocumentId));
                    return;
                }
                this.yukiDocument = yukiDocument;
            }),
        );
    }
}
